import { Add, ArrowBack, Delete, Edit, Image as ImageIcon, VideoLibrary } from '@mui/icons-material'
import { Card, CardContent, Chip, CircularProgress, IconButton, Tooltip } from '@mui/material'
import { assessmentApi, courseApi } from 'configs/api'
import { useCourse } from 'hooks/useCourse'
import { ModuleDialog } from 'layouts/course/components/ModuleDialog'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CourseModule, ModuleContent } from 'slices/courseSlice'
import Box from 'themeComponents/Box'
import Button from 'themeComponents/Button'
import Footer from 'themeComponents/Footer'
import DashboardLayout from 'themeComponents/LayoutContainers/DashboardLayout'
import DashboardNavbar from 'themeComponents/Navbars/DashboardNavbar'
import Typography from 'themeComponents/Typography'
import { CourseData } from './types'
import { ContentDialog } from 'layouts/course/components/ContentDialog'

export default function CourseModules() {
    const navigate = useNavigate();
    const { getCourse } = useCourse();
    const { courseId } = useParams<{ courseId: string }>();

    const [currentCourse, setCurrentCourse] = useState<CourseData | null>(null);
    const [modules, setModules] = useState<CourseModule[]>([]);
    const [assessments, setAssessments] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [savingModule, setSavingModule] = useState(false);
    const [savingContent, setSavingContent] = useState(false);

    const [editingModule, setEditingModule] = useState<CourseModule | null>(null);
    const [editingModuleIndex, setEditingModuleIndex] = useState<number | null>(null);
    const [editingContent, setEditingContent] = useState<{ moduleIndex: number; contentIndex: number; content: ModuleContent } | null>(null);
    const [selectedModuleIndex, setSelectedModuleIndex] = useState<number | null>(null);
    const [openContentDialog, setOpenContentDialog] = useState(false);
    const [openModuleDialog, setOpenModuleDialog] = useState(false);

    const loadCourse = useCallback(async () => {
        if (!courseId) return;
        setLoading(true);
        setError(null);
        try {
            const courseData = await getCourse(courseId);
            const course: CourseData = courseData.data;
            setCurrentCourse(course);
            // Use modules from course API — they already include populated content
            setModules((course.modules || []).map((mod: CourseModule) => ({
                ...mod,
                content: mod.content || [],
            })));
            
            // Fetch assessments for this course so they can be linked to modules
            try {
                const assessmentRes = await assessmentApi.get(`/api/assessments/course/${courseId}`);
                const assessmentList = Array.isArray(assessmentRes.data) 
                    ? assessmentRes.data 
                    : Array.isArray(assessmentRes.data?.data) 
                    ? assessmentRes.data.data 
                    : [];
                setAssessments(assessmentList);
            } catch (err) {
                console.warn('Failed to load assessments for module unlocking:', err);
                setAssessments([]);
            }
        } catch (err) {
            console.error('Failed to load course:', err);
            setError('Failed to load course data. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [courseId, getCourse]);

    useEffect(() => {
        if (!courseId) {
            navigate('/courses');
            return;
        }
        loadCourse();
    }, [courseId, loadCourse, navigate]);

    const handleSaveModule = async (moduleData: Partial<CourseModule>) => {
        if (!courseId) return;
        setSavingModule(true);
        try {
            if (editingModule && editingModuleIndex !== null) {
                await courseApi.put(`/api/course-modules/${editingModule.id}`, {
                    title: moduleData.title,
                    description: moduleData.description,
                    isUnlocked: moduleData.isUnlocked,
                    unlockCondition: moduleData.unlockCondition,
                });
                setModules(prev => {
                    const updated = [...prev];
                    updated[editingModuleIndex] = {
                        ...updated[editingModuleIndex],
                        ...moduleData,
                        content: updated[editingModuleIndex].content,
                    };
                    return updated;
                });
            } else {
                const newModule = {
                    title: moduleData.title || '',
                    description: moduleData.description,
                    order: modules.length,
                    isUnlocked: moduleData.isUnlocked ?? (modules.length === 0),
                    unlockCondition: moduleData.unlockCondition || 'sequential',
                    courseId,
                };
                const response = await courseApi.post('/api/course-modules/', newModule);
                if (response.data) {
                    setModules(prev => [...prev, { ...newModule, id: response.data.id, content: [] }]);
                }
            }
        } catch (err) {
            console.error('Failed to save module:', err);
        } finally {
            setSavingModule(false);
            setOpenModuleDialog(false);
            setEditingModule(null);
            setEditingModuleIndex(null);
        }
    };

    const handleDeleteModule = async (moduleIndex: number) => {
        const module = modules[moduleIndex];
        if (!module.id) return;
        if (!window.confirm(`Delete module "${module.title}"? This cannot be undone.`)) return;
        try {
            await courseApi.delete(`/api/course-modules/${module.id}`);
            setModules(prev => prev.filter((_, i) => i !== moduleIndex));
        } catch (err) {
            console.error('Failed to delete module:', err);
        }
    };

    const handleSaveContent = async (contentData: Partial<ModuleContent>) => {
        if (selectedModuleIndex === null) return;
        setSavingContent(true);

        const toContentUrlArray = (value?: unknown) => {
            if (Array.isArray(value)) {
                const normalized = value
                    .filter((entry: any) => entry?.url?.trim())
                    .map((entry: any) => ({
                        lang: entry.lang || 'en',
                        url: entry.url.trim(),
                    }));

                return normalized.length > 0 ? normalized : undefined;
            }

            if (typeof value === 'string') {
                const trimmed = value.trim();
                return trimmed ? [{ url: trimmed, lang: 'en' }] : undefined;
            }

            return undefined;
        };

        const toVideoUrlArray = (videoUrls?: ModuleContent['videoURL']) => {
            if (!Array.isArray(videoUrls)) return undefined;
            const normalized = videoUrls
                .filter((entry) => entry?.url?.trim())
                .map((entry) => ({
                    lang: entry.lang || 'en',
                    url: entry.url.trim(),
                }));

            return normalized.length > 0 ? normalized : undefined;
        };

        try {
            const module = modules[selectedModuleIndex];
            if (editingContent) {
                const updatePayload = {
                    moduleID: module.id,
                    title: contentData.title || undefined,
                    type: contentData.type || undefined,
                    videoURL: toVideoUrlArray(contentData.videoURL),
                    imageURL: toContentUrlArray(contentData.imageURL),
                    audioURL: toContentUrlArray(contentData.audioURL),
                    content: contentData.content || undefined,
                    order: contentData.order ?? editingContent.content.order,
                    duration: contentData.duration ?? undefined,
                };

                const response = await courseApi.put(`/api/module-contents/${editingContent.content.id}`, updatePayload);
                if (response.data) {
                    setModules(prev => {
                        const updated = [...prev];
                        const contents = [...updated[selectedModuleIndex].content];
                        contents[editingContent.contentIndex] = { ...contents[editingContent.contentIndex], ...contentData };
                        updated[selectedModuleIndex] = { ...updated[selectedModuleIndex], content: contents };
                        return updated;
                    });
                }
            } else {
                const payload = {
                    moduleID: module.id,
                    title: contentData.title || '',
                    type: contentData.type || 'video',
                    videoURL: toVideoUrlArray(contentData.videoURL),
                    imageURL: toContentUrlArray(contentData.imageURL),
                    audioURL: toContentUrlArray(contentData.audioURL),
                    order: module.content.length,
                    duration: contentData.duration || undefined,
                };
                const response = await courseApi.post('/api/module-contents/', payload);
                if (response.data) {
                    const savedContent: ModuleContent = {
                        id: response.data.id,
                        moduleID: payload.moduleID,
                        title: payload.title,
                        type: payload.type,
                        videoURL: payload.videoURL as ModuleContent['videoURL'],
                        imageURL: payload.imageURL?.[0]?.url,
                        audioURL: payload.audioURL?.[0]?.url,
                        order: payload.order,
                        duration: payload.duration,
                    };
                    setModules(prev => {
                        const updated = [...prev];
                        updated[selectedModuleIndex] = {
                            ...updated[selectedModuleIndex],
                            content: [...updated[selectedModuleIndex].content, savedContent],
                        };
                        return updated;
                    });
                }
            }
        } catch (err) {
            console.error('Failed to save content:', err);
        } finally {
            setSavingContent(false);
            setOpenContentDialog(false);
            setEditingContent(null);
            setSelectedModuleIndex(null);
        }
    };

    const handleDeleteContent = async (moduleIndex: number, contentIndex: number) => {
        const item = modules[moduleIndex].content[contentIndex];
        if (!item.id) return;
        if (!window.confirm(`Delete "${item.title}"?`)) return;
        try {
            await courseApi.delete(`/api/module-contents/${item.id}`);
            setModules(prev => {
                const updated = [...prev];
                updated[moduleIndex] = {
                    ...updated[moduleIndex],
                    content: updated[moduleIndex].content.filter((_, i) => i !== contentIndex),
                };
                return updated;
            });
        } catch (err) {
            console.error('Failed to delete content:', err);
        }
    };

    if (loading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <Box display="flex" justifyContent="center" alignItems="center" py={10}>
                    <CircularProgress />
                </Box>
                <Footer />
            </DashboardLayout>
        );
    }

    if (error) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <Box display="flex" justifyContent="center" alignItems="center" py={10} flexDirection="column" gap={2}>
                    <Typography color="error">{error}</Typography>
                    <Button variant="gradient" color="info" onClick={loadCourse}>Retry</Button>
                </Box>
                <Footer />
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <ModuleDialog
                open={openModuleDialog}
                onClose={() => {
                    setOpenModuleDialog(false);
                    setEditingModule(null);
                    setEditingModuleIndex(null);
                }}
                onSave={handleSaveModule}
                module={editingModule}
            />
            <ContentDialog
                open={openContentDialog}
                onClose={() => {
                    setOpenContentDialog(false);
                    setEditingContent(null);
                    setSelectedModuleIndex(null);
                }}
                onSave={handleSaveContent}
                content={editingContent?.content}
                languages={currentCourse?.courseLanguages}
            />
            <Box py={3}>
                <Box mb={3} display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={() => navigate(`/courses/${courseId}/edit`)}>
                        <ArrowBack />
                    </IconButton>
                    <Box>
                        <Typography variant="h4" fontWeight="bold">
                            Course Modules
                        </Typography>
                        <Box mt={0.5} display="flex" gap={1} flexWrap="wrap">
                            <Chip size="small" color="info" variant="outlined" label="Step 2 of 3" />
                            <Chip size="small" color="secondary" variant="outlined" label="Modules and content" />
                        </Box>
                        {currentCourse?.title && (
                            <Typography variant="body2" color="text">
                                {currentCourse.title}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                    <Typography variant="subtitle1" fontWeight="medium">
                        Modules ({modules.length})
                    </Typography>
                    <Button
                        variant="gradient"
                        color="info"
                        size="small"
                        onClick={() => {
                            setEditingModule(null);
                            setEditingModuleIndex(null);
                            setOpenModuleDialog(true);
                        }}
                        disabled={savingModule}
                    >
                        <Add sx={{ mr: 1 }} /> Add Module
                    </Button>
                </Box>

                {modules.length === 0 ? (
                    <Box textAlign="center" py={6}>
                        <Typography variant="body1" color="text" mb={2}>
                            No modules yet. Add your first module to get started.
                        </Typography>
                    </Box>
                ) : (
                    <Box>
                        {modules.map((module, moduleIndex) => (
                            (() => {
                                const linkedAssessment = assessments.find((assessment) => assessment?.unlocksModuleID === module.id);
                                return (
                            <Card key={module.id || moduleIndex} sx={{ mb: 2, border: '1px solid', borderColor: 'divider', transition: 'all 0.2s ease', '&:hover': { boxShadow: 6, borderColor: 'info.main' } }}>
                                <CardContent>
                                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                                        <Box flex={1}>
                                            <Typography variant="h6">{module.title}</Typography>
                                            {module.description && (
                                                <Typography variant="body2" color="text" mt={0.5}>
                                                    {module.description}
                                                </Typography>
                                            )}
                                            <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                                                <Chip label={`Order: ${module.order + 1}`} size="small" color="primary" variant="outlined" />
                                                <Chip label={module.unlockCondition || 'sequential'} size="small" color="secondary" variant="outlined" />
                                                <Chip label={module.isUnlocked ? 'Unlocked' : 'Locked'} size="small" color={module.isUnlocked ? 'success' : 'default'} variant="outlined" />
                                                {module.unlockCondition === 'assessment_passed' && (
                                                    <Chip
                                                        label={linkedAssessment ? `Assessment: ${linkedAssessment.title}` : 'Assessment link not set'}
                                                        size="small"
                                                        color={linkedAssessment ? 'info' : 'warning'}
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Box>
                                        </Box>
                                        <Box display="flex" gap={0.5} ml={1}>
                                            <Tooltip title="Edit module">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setEditingModule(module);
                                                        setEditingModuleIndex(moduleIndex);
                                                        setOpenModuleDialog(true);
                                                    }}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete module">
                                                <IconButton size="small" onClick={() => handleDeleteModule(moduleIndex)}>
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>

                                    <Box>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                            <Typography variant="subtitle2" fontWeight="medium">
                                                Content ({module.content.length})
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                color="info"
                                                size="small"
                                                onClick={() => {
                                                    setSelectedModuleIndex(moduleIndex);
                                                    setEditingContent(null);
                                                    setOpenContentDialog(true);
                                                }}
                                                disabled={savingContent}
                                            >
                                                <Add sx={{ mr: 0.5, fontSize: 16 }} /> Add Content
                                            </Button>
                                        </Box>

                                        {module.content.length === 0 ? (
                                            <Typography variant="caption" color="text">
                                                No content yet. Click &quot;Add Content&quot; to add videos or images.
                                            </Typography>
                                        ) : (
                                            <Box display="flex" flexDirection="column" gap={1}>
                                                {module.content.map((item, contentIndex) => (
                                                    <Box
                                                        key={item.id || contentIndex}
                                                        display="flex"
                                                        justifyContent="space-between"
                                                        alignItems="center"
                                                        p={1.5}
                                                        sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
                                                    >
                                                        <Box display="flex" alignItems="center" gap={1}>
                                                            {item.type === 'video' ? (
                                                                <VideoLibrary fontSize="small" color="info" />
                                                            ) : (
                                                                <ImageIcon fontSize="small" color="secondary" />
                                                            )}
                                                            <Box>
                                                                <Typography variant="body2" fontWeight="medium">{item.title}</Typography>
                                                                <Typography variant="caption" color="text">
                                                                    {item.type === 'video' ? 'Video' : 'Image + Audio'}
                                                                    {item.videoURL && item.videoURL.length > 0 && ` • ${item.videoURL.length} language(s)`}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        <Box display="flex" gap={0.5}>
                                                            <Tooltip title="Edit content">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => {
                                                                        setSelectedModuleIndex(moduleIndex);
                                                                        setEditingContent({ moduleIndex, contentIndex, content: item });
                                                                        setOpenContentDialog(true);
                                                                    }}
                                                                >
                                                                    <Edit fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Delete content">
                                                                <IconButton
                                                                    size="small"
                                                                    onClick={() => handleDeleteContent(moduleIndex, contentIndex)}
                                                                >
                                                                    <Delete fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                </CardContent>
                            </Card>
                                );
                            })()
                        ))}
                    </Box>
                )}
            </Box>
            <Box display="flex" justifyContent="flex-end" mt={4} mb={2}>
                <Button
                    variant="gradient"
                    color="info"
                    size="large"
                    onClick={() => navigate(`/courses/${courseId}/assessments/list`)}
                >
                    Next
                </Button>
            </Box>
            <Footer />
        </DashboardLayout>
    );
}
